<template>
	<transition name="slide">
		<div 
			v-if="open" 
			class="explanation"
			:class="[
        `${mainContent.heading}`,
        { 'explanation--wide': content.video, 'lightbox-open': lightboxOpen }
      ]"
		>

      <div class="explanation__header">
				<h2>{{ $t(title).replaceAll('<br>', '') }}</h2>
			</div>

      <div v-if="mainContent.subExplanations?.length > 1">
        <div
          class="explanation__swiper">
          <div class="swiper__nav swiper__nav--prev" :class="{ hidden: activeSubIndex === 0 }" @click="prevSlide">
            <svg class="swiper__icon"><use href="#sprite-arrow-thin-right"></use></svg>
          </div>
          <div class="swiper__nav swiper__nav--next" :class="{ hidden: activeSubIndex === mainContent.subExplanations.length - 1  }" @click="nextSlide">
            <svg class="swiper__icon"><use href="#sprite-arrow-thin-right"></use></svg>
          </div>
          <swiper
            ref="swiper"
            :key="swiperKey"
            slides-per-view="1"
            :allow-touch-move="false"
          >
            <swiper-slide
              v-for="(item, index) in mainContent.subExplanations"
              :key="index" 
            >
              <div class="explanation__image">
                <div class="explanation__gallery">
                  <img :src="'images/' + productConfig.explanations[item].images[0]" alt="">
                </div>
              </div>
            </swiper-slide>
          </swiper>
        </div>
        <div
          class="explanation__text"
          :class="{ 'explanation__text--highlighted': content.subExplanations || activeSubExplanation }"
        >
          <div class="explanation__head">
            <h3>{{ content.heading }}</h3>
            <span v-if="mainContent.subExplanations?.length">{{ mainContent.subExplanations.indexOf(activeSubExplanation) + 1 }}/{{ mainContent.subExplanations.length }}</span>
          </div>
          <p v-if="content.description" v-html="content.description" />
        </div>
      </div>

      <div v-else>

        <div class="explanation__image">
          <div v-if="content.images" class="explanation__gallery">
            <img :src="'images/' + content.images[0]" alt="">
          </div>
          <div v-if="content.video" class="explanation__video-wrapper">
            <div class="explanation__video">
              <transition name="fade">
                <div v-if="lightboxOpen" class="explanation__video-overlay"></div>
              </transition>
              <video
								ref="videoEl"
								:src="`videos/${content.video}`"
								:poster="content.videoPoster ? `images/${content.videoPoster}` : undefined"
								disablepictureinpicture
							/>
              <div
                class="explanation__video-controls"
                :class="{ playing: videoPlaying }"
                @click="videoClickHandler"
                v-click-outside="closeLightbox"
              >
                <div class="play">
                  <svg class="icon icon--white icon--75">
                    <use :href="videoPlaying ? '#icon-pause' : '#icon-play'"></use>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="explanation__text"
          :class="{ 'explanation__text--highlighted': content.subExplanations || activeSubExplanation }"
        >
          <h3>{{ content.heading }}</h3>
          <p v-if="content.description" v-html="content.description" />
        </div>

      </div>

			<div class="explanation__content">
				<ul
					v-if="mainContent.subExplanations?.length > 1"
					class="explanation__list"
				>
					<li
						v-for="item in mainContent.subExplanations"
						:key="item"
						class="explanation__list-item"
						:class="{ active: activeSubExplanation === item }"
						@click="setActiveSub(item)"
					>
						{{ productConfig.explanations[item].heading }}
					</li>
				</ul>
			</div>

			<svg
				v-if="lightboxOpen"
				class="icon icon--close"
				@click="close"
			>
				<use :href="'#icon-close'"></use>
			</svg>

		</div>
	</transition>
</template>

<script>
import { mapState } from 'vuex'

import { Swiper, SwiperSlide } from 'swiper/vue'

export default {
	name: 'ExplanationPane',
  components: {
		Swiper,
		SwiperSlide,
	},
	data() {
		return {
			videoPlaying: false,
			lightboxOpen: false,
			listIntersectionObserver: null,
      swiperKey: 0,
		}
	},
	computed: {
		...mapState({
			open: state => state.explanationPaneOpen,
			title: state => state.navigation.activeSecondary,
			mainContent: state => state.explanationPaneContent,
			activeSubExplanation: state => state.explanationPaneSub,
			productConfig: state => state.productConfig
		}),

		content() {
			return this.activeSubExplanation ? this.productConfig.explanations[this.activeSubExplanation] : this.mainContent
		},

		activeSubIndex() {
			return this.mainContent.subExplanations.indexOf(this.activeSubExplanation)
		}
	},
	watch: {
		open() {
			if (!this.mainContent.description && this.mainContent.subExplanations?.length) {
				this.setActiveSubExplanation(this.mainContent.subExplanations[0])
			}
			this.$nextTick(() => {
				this.setupSubExplanationsScroll()
			})
		},

    activeSubExplanation() {
      if (this.$refs.swiper && this.mainContent.subExplanations) {
        this.$refs.swiper.$el.swiper.slideTo(this.activeSubIndex)
      }
    },

		mainContent(newVal) {
      if (this.$refs.swiper) {
        this.swiperKey += 1;  
      }

			this.setActiveSubExplanation(newVal.description ? null : newVal.subExplanations[0])
			this.$nextTick(() => {
				this.setupSubExplanationsScroll()
			})

			if (newVal.camera) {
				this.$bus.emit('requestCameraPosition', newVal.camera)
			}
		}
	},
	methods: {
    nextSlide() {
      this.$refs.swiper.$el.swiper.slideNext()
      if (this.activeSubIndex + 1 < this.mainContent.subExplanations.length) {
        this.setActiveSubExplanation(this.mainContent.subExplanations[this.activeSubIndex+1])
      }
    },
    prevSlide() {
      this.$refs.swiper.$el.swiper.slidePrev()
      if (this.activeSubIndex) {
        this.setActiveSubExplanation(this.mainContent.subExplanations[this.activeSubIndex-1])
      }
    },
    setActiveSub(name) {
      this.$refs.swiper.$el.swiper.slideTo(this.mainContent.subExplanations.indexOf(name))
      this.setActiveSubExplanation(name)
    },
		videoClickHandler() {
			if (!this.lightboxOpen) {
				this.lightboxOpen = true
			}
			this.triggerVideoPlayback()
		},

		triggerVideoPlayback() {
			if (this.videoPlaying) {
				this.$refs.videoEl.pause()
				this.videoPlaying = false
			} else {
				this.$refs.videoEl.play()
				this.videoPlaying = true
			}
		},

		setupSubExplanationsScroll() {
			if (this.listIntersectionObserver) {
				this.listIntersectionObserver.disconnect()
			}

			if (this.open) {
				this.listIntersectionObserver = new IntersectionObserver(entries => {
					entries.forEach(entry => {
						entry.target.style.opacity = Math.pow(entry.intersectionRatio, 3)
					})
				}, {
					threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
				})
	
				const listItems = document.querySelectorAll('.explanation__list-item')
				listItems.forEach(el => {
					this.listIntersectionObserver.observe(el)
				})
			}
		},

		setActiveSubExplanation(name) {
			this.$store.commit('setExplanationPaneSub', name)

			const explanationDefinition = this.productConfig.explanations[name]
			if (explanationDefinition?.camera) {
				this.$bus.emit('requestCameraPosition', explanationDefinition.camera)
			}
			if (explanationDefinition?.animation) {
				const animation = explanationDefinition.animation
				if (typeof animation === 'object') {
					animation.forEach(name => {
						this.$bus.emit('requestModelAnimation', { name })
					})
				} else {
					this.$bus.emit('requestModelAnimation', { name: animation })
				}
			} else {
				this.$bus.emit('requestModelAnimationsReset')
			}
		},

		close() {
			if (this.lightboxOpen) {
				this.closeLightbox()
			} else {
				this.$store.commit('setExplanationPaneOpen', false)
				this.$store.commit('navigation/setActiveSecondary', null)
			}
		},

		closeLightbox() {
			this.$refs.videoEl.pause()
			this.videoPlaying = false
			this.lightboxOpen = false
		}
	}
}
</script>

<style lang="scss">
.explanation {
	position: absolute;
	right: 0;
	top: 0;
	height: 100vh;
	width: 25vw;
	min-width: 300px;
	max-width: 540px;
	background-color: $clrWhiteAlpha50;
	box-shadow: 0 0 30px #00000020;
	backdrop-filter: blur(15px);
	transition: transform 400ms ease;
	perspective: 1000px;
	backface-visibility: hidden;
	display: flex;
	flex-direction: column;
	z-index: 100;

  @media screen and (min-width: 1000px) {
    width: 30vw;
  }

  @media screen and (min-width: 1200px) {
    width: 35vw;
  }

  @media screen and (min-width: 1400px) {
    width: 40vw;
  }

	// &--wide {
	// 	width: 70vw;
	// 	max-width: 800px;
	// }
	&.slide-enter-from {
		transform: translate3d(100%, 0, 0) !important;
	}
	&.slide-leave-to {
		transform: translate3d(100%, 0, 0);
	}

	.icon--close {
		position: absolute;
		top: 5px;
		right: 5px;
		width: 36px;
		height: 36px;
		border-radius: 20px;
		border: 1px solid transparent;
		background-color: #ececec;
		cursor: pointer;
		&:hover {
			border-color: $clrRed;
		}
	}

	&__header {
		background-color: $clrRed;
		h2 {
			color: $clrWhite;
			padding: 10px 25px;
			text-transform: capitalize;
		}
	}

	&__image {
		position: relative;
		line-height: 0;
	}

	&__gallery {
		width: 100%;
		padding-top: 60%;
		@media screen and (max-height: 640px) {
			padding-top: 50%;
		}


		img {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	&__video-wrapper {
		position: relative;
		padding-top: 56.25%;
		background-color: $clrGrey250;
	}
	&__video-overlay {
		position: fixed;
		top: 0;
		right: 0;
		width: 100vw;
		height: 100vh;
		background-color: #FFF;
		opacity: 0.8;
		transition: opacity 200ms linear;
		&.fade-enter-from, &.fade-leave-to {
			opacity: 0;
		}
	}
	&__video {
		position: absolute;
		top: 0;
		right: 0;
		width: 100%;
		height: 100%;
		transition: all 200ms ease;
		video {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
		.lightbox-open & {
			width: 80vw;
			height: calc(80vw * 0.5625);
			right: calc(50vw - 40vw);
			top: calc(50vh - 80vw * 0.28 - 56px); // 56px to compensate for the height of explanation header
		}
	}

	&__video-controls {
		position: absolute;
		display: flex;
		align-items: center;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background-color: $clrBlackAlpha70;
		transition: opacity 200ms ease;
		cursor: pointer;
		opacity: 0;
		.play {
			margin: 0 auto;
		}
		&:not(.playing) {
			opacity: 1;
		}
	}

	&__content {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	&__text {
		padding: 25px;

		h3 {
			margin: 0 0 10px;
			&:empty { display: none; }
		}
		strong {
			color: $clrRed;
		}
		big {
			font-size: 1.2em;
		}
		&--highlighted {
			background-color: $clrWhiteAlpha50;

			&:has(p) {
				height: 159px;
			}
		}
	}

  &__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    color: $clrRed;
    h3 {
      margin-right: 8px;
    }
  }

	&__list {
		margin: 0 20px 0 0;
		padding: 0 25px;
		list-style: none;
		overflow-y: auto;
		// mask-image: linear-gradient(transparent 0%, #333 8%, black 10%, black 90%, #333 92%, transparent 100%);
		scroll-snap-type: y mandatory;
		scrollbar-width: thin;
		scrollbar-color: $clrGrey250 $clrGrey100;
		&::-webkit-scrollbar {
			width: 7px;
		}
		&::-webkit-scrollbar-track {
			border-radius: 20px;
			background-color: $clrGrey150;
			margin: 12px 0;
		}
		&::-webkit-scrollbar-thumb {
			border-radius: 20px;
			background-color: $clrGrey250;
		}

		&-item {
			cursor: pointer;
			padding: 10px 0;
			color: $clrGrey300;
			scroll-snap-align: start;

			&:hover {
				color: $clrGrey450;
			}
			&.active {
				color: $clrGrey200;
			}
		}
	}

	&.lightbox-open {
		.icon--close {
			background-color: $clrGrey300;
			border-color: $clrGrey300;
			fill: $clrWhite;
			&:hover {
				background-color: $clrGrey400;
				border-color: $clrGrey400;
			}
		}
	}

}
.explanation__swiper {
  position: relative;
}
.swiper__nav {
  position: absolute;
  z-index: 10;
  cursor: pointer;
  top: 50%;
  transform: translateY(-50%);
  background-color: $clrRed;
  width: 32px;
  height: 32px;
  border-radius: 100%;

	&.hidden {
		display: none;
	}

  &.swiper__nav--next {
    right: 16px;
  }

  &.swiper__nav--prev {
    left: 16px;

    .swiper__icon {
      transform: rotate(180deg) translate(50%, 50%);
    }
  }
}

.swiper__icon {
  position: absolute;
  width: 65%;
  height: 65%;
  color: #fff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
